/* eslint-disable */
import Resizer from 'react-image-file-resizer'
import * as constants from '../../irmlib/src/common/constants'
import { ignoreBarcodeScanCharacters } from '../common/appConstants.js'
import * as localGdeTransactions from '../firestore/localGdeTransactions'
import * as localGdeTransactionLite from '../firestore/localGdeTransactionsLite'

const LOCAL_AI_ASSET_IMG = constants.LOCAL_AI_ASSET_IMG

const BULK_UPLOAD_OPEN_STATUSES = [
  constants.activityStatus.pending,
  constants.activityStatus.inProgress,
]

/**
 * Resolve activity by request ID + SKP box number. Only pending / In Progress
 * activities without IDP failure are accepted (aligned with selectable requests in LocalActivityAddImages).
 */
export const resolveBulkUploadActivity = async (requestIdRaw, boxIdRaw, t) => {
  const requestId = requestIdRaw.trim()
  let boxId = boxIdRaw.trim().toUpperCase()
  if (!requestId || !boxId) {
    throw new Error(t('smartCatalog.bulkNatsRequestAndBoxRequired'))
  }
  let snap = await localGdeTransactionLite.getBoxActivity(requestId, boxId)
  if (
    snap.docs.length === 0 &&
    ignoreBarcodeScanCharacters.indexOf(Array.from(boxId)[0]?.toUpperCase()) !== -1
  ) {
    const stripped = boxId.substring(1)
    const retrySnap = await localGdeTransactionLite.getBoxActivity(requestId, stripped)
    if (retrySnap.docs.length > 0) {
      snap = retrySnap
      boxId = stripped
    }
  }
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
  if (!rows.length) {
    throw new Error(t('smartCatalog.bulkNatsRequestBoxNotFound'))
  }
  const open = rows.filter(
    (r) => BULK_UPLOAD_OPEN_STATUSES.includes(r.status) && !r.isIdpFail
  )
  if (!open.length) {
    throw new Error(t('smartCatalog.bulkNatsBoxClosed'))
  }
  if (open.length > 1) {
    throw new Error(t('smartCatalog.bulkNatsMultipleOpenBoxes'))
  }
  const activityId = open[0].id
  const activityData = await localGdeTransactions.getActivityByID(activityId)
  if (!activityData) {
    throw new Error(t('smartCatalog.bulkNatsActivityNotFound'))
  }
  return { activityId, activityData, resolvedBoxId: boxId }
}

/**
 * Resize image the same way as LocalActivityAddImages (max 1500 JPEG 80%).
 */
export const resizeImageFile = (file) =>
  new Promise((resolve, reject) => {
    Resizer.imageFileResizer(
      file,
      1500,
      1500,
      'JPEG',
      80,
      0,
      (uri) => {
        try {
          const arr = uri.split(',')
          const mime = arr[0].match(/:(.*?);/)[1]
          const bstr = atob(arr[1])
          let n = bstr.length
          const u8arr = new Uint8Array(n)
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n)
          }
          const name = `${(file.name || 'upload').replace(/\.[^.]+$/, '')}.jpg`
          resolve(new File([u8arr], name, { type: mime }))
        } catch (e) {
          reject(e)
        }
      },
      'base64'
    )
  })

/**
 * Ensure natsService.serverURL is set (same pattern as NatsProvider).
 */
export const ensureNatsServerUrl = async (natsService) => {
  if (!natsService?.serverURL) {
    const localAiServerURL = await localGdeTransactions.getLocalServerUrl()
    natsService.serverURL = localAiServerURL.origin
  }
  return natsService.serverURL
}

const buildImagePayload = ({
  activityData,
  activityId,
  assetType,
  workerId,
  username,
  imgName,
  imgNuid,
  fileSequence,
}) => {
  const unlockedFields = activityData?.template
  if (!unlockedFields || typeof unlockedFields !== 'object' || Object.keys(unlockedFields).length === 0) {
    throw new Error('Activity is missing template data')
  }
  const refTemplateId = activityData.templateId
  const RefImage =
    activityData.initialImgReferenceNats || activityData.imgReference || ''
  const RefImageGsPath = activityData.imgReferenceURI || ''

  return {
    accountLevel1: activityData.accountLevel1 || '',
    accountLevel2: activityData.accountLevel2 || '',
    accountLevel3: activityData.accountLevel3 || '',
    actionTs: new Date().toISOString(),
    activityId,
    docId: activityId,
    assetType,
    fileSequence: fileSequence || '',
    isException: false,
    exceptionNote: '',
    countryCode: activityData.countryCode,
    building: activityData.building || '',
    custBoxNbr: activityData.custBoxNbr || '',
    district: activityData.district || '',
    lob: activityData.lob || '',
    orderId: activityData.orderId || '',
    requestId: activityData.requestId || '',
    templateId: refTemplateId,
    skpBoxNbr: activityData.skpBoxNbr || '',
    status: constants.activityStatus.submitted,
    template: unlockedFields,
    templateLocked: activityData.templateLocked || {},
    imgReference: RefImage || '',
    imgReferenceURI: RefImageGsPath || '',
    imgDetails: {
      imgPath: [imgName],
      imgNUID: [imgNuid],
      numOfBoxes: 0,
      numOfFiles: 1,
    },
    isProcessed: false,
    isPublished: false,
    isIdpFail: false,
    templateName: activityData.templateName || '',
    createdTS: new Date().toISOString(),
    updatedTS: new Date().toISOString(),
    username,
    workerId,
    srcSystem: 'web',
    isActive: true,
    inBQ: false,
    idp: {
      statusCode: '',
      status: '',
      exception: '',
      hitl: {},
    },
    idpResponse: {},
    idp_message_id: '',
    messageId: '',
    processedAt: '',
    publishedAt: '',
    wmsCode: activityData.wmsCode || '',
    wmsRegion: activityData.wmsRegion || '',
    dataLocation: activityData.dataLocation || '',
    activityName: activityData.activityName || '',
    activityType: activityData.activityType || 'BAU',
  }
}

/**
 * One image: NATS object-store upload -> Firestore image doc save.
 */
export const uploadAndSaveOneImage = async ({
  natsService,
  file,
  activityData,
  activityId,
  workerId,
  username,
}) => {
  const serverURL = await ensureNatsServerUrl(natsService)
  const resized = await resizeImageFile(file)
  const rename = `nats_assetImg_${workerId}_${Date.now()}.jpg`
  const renamedFile = new File([resized], rename, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  })

  const uploadInfo = await natsService.uploadRefImg(
    serverURL,
    LOCAL_AI_ASSET_IMG,
    renamedFile
  )
  if (!uploadInfo?.name || !uploadInfo?.nuid) {
    throw new Error('NATS object store upload failed')
  }

  const imageData = buildImagePayload({
    activityData,
    activityId,
    assetType: activityData.assetType,
    workerId,
    username,
    imgName: uploadInfo.name,
    imgNuid: uploadInfo.nuid,
    fileSequence: activityData.fileSequence,
  })

  const imgId = await localGdeTransactions.saveActivityImages(imageData)
  if (!imgId) {
    throw new Error('Failed to save image document')
  }

  const finalDoc = await localGdeTransactions.getImageData(imgId)
  if (!finalDoc) {
    throw new Error('Could not read saved image document')
  }
  let firestoreImage = { id: imgId, ...finalDoc }

  const updateAfterPublish = {
    isPublished: true,
    isProcessed: false,
    publishedAt: new Date().toISOString(),
  }

  let msgId = ''
  let publishFailed = false
  if (natsService) {
    try {
      const { msgId: publishedMsgId } = await natsService.publishImgData(
        constants.LOCAL_NATS_SERVER,
        { ...firestoreImage, ...updateAfterPublish }
      )
      msgId = publishedMsgId || ''
      if (msgId) {
        await localGdeTransactions.updateImage(imgId, {
          ...updateAfterPublish,
          id: imgId,
          messageId: msgId,
        })
      } else {
        await localGdeTransactions.updateImage(imgId, { ...updateAfterPublish, id: imgId })
      }
    } catch (publishError) {
      publishFailed = true
      console.error('Bulk image NATS publish failed:', publishError)
      try {
        await localGdeTransactions.updateImage(imgId, { ...updateAfterPublish, id: imgId })
      } catch (updateError) {
        console.error('Bulk image update after publish failure:', updateError)
      }
    }
  } else {
    await localGdeTransactions.updateImage(imgId, { ...updateAfterPublish, id: imgId })
  }

  const finalAfterPublish = await localGdeTransactions.getImageData(imgId)
  firestoreImage = finalAfterPublish ? { id: imgId, ...finalAfterPublish } : firestoreImage

  return {
    imgId,
    natsName: uploadInfo.name,
    natsNuid: uploadInfo.nuid,
    firestoreImage,
    msgId,
    publishFailed,
  }
}

export const updateActivityAfterBulkUpload = async (activityId) => {
  await localGdeTransactions.updateActivityData(activityId, {
    updatedTS: new Date().toISOString(),
  })
}
